/**
 * S-27 › Game features (/settings/features) — per-user opt-in toggles for the
 * OPTIONAL mechanics, mirroring the server's user_settings booleans 1:1
 * (migration 0002 + the takeover_offers / read_receipts / presence_visible
 * columns that never had a screen).
 *
 * GET /settings hydrates; each flip PATCHes ONLY its key (optimistic, reverts
 * on error — same contract as the notifications screen). The server stays
 * authoritative: SM-2/5/6/7/8 refuse or skip when a mechanic is OFF; this
 * screen and the affordance-hiding (deck 💀, pit entry, berserker activate)
 * are UX courtesy on top.
 */
import { useEffect, useState } from "react";
import { resolveCopy } from "@humble/shared";
import { Card, Mascot, Skeleton, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { featureToggleStore, refreshFeatureToggles } from "./use-feature-toggles.js";
import "./features.css";

type ToggleKey =
  | "super_reject_enabled"
  | "berserker_enabled"
  | "killstreak_enabled"
  | "roulette_enabled"
  | "takeover_offers"
  | "read_receipts"
  | "presence_visible";

interface ToggleSpec {
  key: ToggleKey;
  label: string; // © key
  desc: string; // © key
}

/** The arcade — the four 0002 mechanics + the takeover_offers precedent. */
const GAME_TOGGLES: ToggleSpec[] = [
  { key: "super_reject_enabled", label: "features.super_reject_label", desc: "features.super_reject_desc" },
  { key: "berserker_enabled", label: "features.berserker_label", desc: "features.berserker_desc" },
  { key: "killstreak_enabled", label: "features.killstreak_label", desc: "features.killstreak_desc" },
  { key: "roulette_enabled", label: "features.roulette_label", desc: "features.roulette_desc" },
  { key: "takeover_offers", label: "features.takeover_label", desc: "features.takeover_desc" },
];

/** The reciprocal chat/presence pair (#50/#51). */
const SOCIAL_TOGGLES: ToggleSpec[] = [
  { key: "read_receipts", label: "features.read_receipts_label", desc: "features.read_receipts_desc" },
  { key: "presence_visible", label: "features.presence_label", desc: "features.presence_desc" },
];

export function FeatureTogglesScreen() {
  const [values, setValues] = useState<Record<ToggleKey, boolean> | null>(null);

  useEffect(() => {
    api
      .get("/settings")
      .then((s) => {
        const v = s as Partial<Record<ToggleKey, boolean>>;
        setValues({
          super_reject_enabled: v.super_reject_enabled ?? true,
          berserker_enabled: v.berserker_enabled ?? true,
          killstreak_enabled: v.killstreak_enabled ?? true,
          roulette_enabled: v.roulette_enabled ?? true,
          takeover_offers: v.takeover_offers ?? true,
          read_receipts: v.read_receipts ?? true,
          presence_visible: v.presence_visible ?? true,
        });
      })
      .catch((err) => {
        if (isApiError(err)) toast(err.message, "error");
        setValues(null);
      });
  }, []);

  const flip = async (key: ToggleKey, enabled: boolean) => {
    setValues((prev) => (prev ? { ...prev, [key]: enabled } : prev)); // optimistic
    try {
      await api.patch("/settings", { body: { [key]: enabled } as never });
      featureToggleStore.set({ [key]: enabled, loaded: true }); // deck/pit/berserker follow live
      toast(resolveCopy("features.saved") ?? "Saved.", "success");
    } catch (err) {
      setValues((prev) => (prev ? { ...prev, [key]: !enabled } : prev)); // revert
      void refreshFeatureToggles();
      toast(
        isApiError(err) ? err.message : (resolveCopy("features.save_failed") ?? "Couldn't save."),
        "error",
      );
    }
  };

  const row = (spec: ToggleSpec) => (
    <label key={spec.key} className="feats__row" data-testid={`feature-toggle-${spec.key}`}>
      <span className="feats__row-label">
        {resolveCopy(spec.label)}
        <span className="feats__row-note">{resolveCopy(spec.desc)}</span>
      </span>
      <input
        type="checkbox"
        className="feats__switch"
        checked={values?.[spec.key] ?? true}
        onChange={(e) => void flip(spec.key, e.target.checked)}
      />
    </label>
  );

  return (
    <div className="feats" data-screen="S-27-features">
      <header className="feats__head">
        <p className="h-caps feats__kicker">{resolveCopy("features.kicker")}</p>
        <h1 className="type-display-3">{resolveCopy("features.title") ?? "Game features"}</h1>
        <p className="type-editorial feats__lede">{resolveCopy("features.lede")}</p>
      </header>

      {values === null ? (
        <>
          <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 14px" }}>
            <Mascot mood="loading" />
          </div>
          <Skeleton height={220} radius={20} style={{ marginBottom: 12 }} />
          <Skeleton height={110} radius={20} />
        </>
      ) : (
        <>
          <Card padding="md" style={{ marginBottom: 12 }}>
            <h2 className="feats__h2">Game mechanics</h2>
            {GAME_TOGGLES.map(row)}
          </Card>
          <Card padding="md">
            <h2 className="feats__h2">Chat &amp; presence</h2>
            {SOCIAL_TOGGLES.map(row)}
          </Card>
        </>
      )}
    </div>
  );
}
